import { queryKeys } from '@qse/api'
import { useIsOverQuota } from '@qse/stores'
import { useProgressTrackerAdapter } from '@qse/ui'
import { initiateFolderTreeWithTracking } from '@qse/ui/src/hooks'
import { type FileItem } from '@qse/utils'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { showMultiFolderModal } from '../components/MultiFolderUploadModal'

// Utility function to check if a file should be filtered out (hidden or system files)
const shouldFilterFile = (fileName: string): boolean => {
  const isHiddenFile = fileName.startsWith('.')
  const isSystemFile = fileName === 'Thumbs.db' || fileName === 'desktop.ini'

  return isHiddenFile || isSystemFile
}

// ───────────────────────────────────────────────────────────────────────────────
// Types for DataTransfer items (webkitGetAsEntry)
// ───────────────────────────────────────────────────────────────────────────────

type AnyEntry = FileSystemEntry & {
  isDirectory: boolean
  isFile: boolean
  createReader?: () => FileSystemDirectoryReader
  file?: (cb: (f: File) => void, err?: (e: any) => void) => void
}

function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const out: FileSystemEntry[] = []
    const step = () => {
      reader.readEntries(
        batch => {
          if (!batch.length) return resolve(out)
          out.push(...batch)
          step()
        },
        err => reject(err),
      )
    }

    step()
  })
}

// ───────────────────────────────────────────────────────────────────────────────
// Limits / Budget
// ───────────────────────────────────────────────────────────────────────────────

const FOLDER_UPLOAD_LIMITS = {
  MAX_FILES: 5000,
  MAX_DEPTH: 50,
  SKIP_DIRS: ['node_modules', '.git', '.yarn', 'dist', 'build', '.next', 'coverage'],
}

interface Budget {
  filesEmitted: number
  maxFiles: number
}

// ───────────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────────

async function countFilesInDirectory(entry: AnyEntry, depth = 0): Promise<number> {
  const name = entry.name || ''

  if (depth > FOLDER_UPLOAD_LIMITS.MAX_DEPTH) return 0
  if (FOLDER_UPLOAD_LIMITS.SKIP_DIRS.includes(name)) return 0

  if (entry.isDirectory) {
    const reader = entry.createReader!()
    const children = await readAllEntries(reader)
    let totalFiles = 0

    for (const child of children as AnyEntry[]) {
      totalFiles += await countFilesInDirectory(child, depth + 1)
      if (totalFiles > FOLDER_UPLOAD_LIMITS.MAX_FILES) break
    }

    return totalFiles
  } else {
    return 1
  }
}

function setRelativePath(file: File, relPath: string) {
  try {
    Object.defineProperty(file, 'webkitRelativePath', { value: relPath, configurable: true })
  } catch {
    // non-fatal
  }
  ;(file as any).path = relPath
}

type DirTree = { name: string; children?: DirTree[] }

/** Walk a directory entry and return files (+ a pure directory tree). No placeholders. */
async function walkDirectory(
  entry: AnyEntry,
  relRoot: string, // e.g. entry.name
  depth = 0,
  budget: Budget = { filesEmitted: 0, maxFiles: FOLDER_UPLOAD_LIMITS.MAX_FILES },
): Promise<{ files: File[]; tree: DirTree }> {
  const name = entry.name || ''

  if (depth > FOLDER_UPLOAD_LIMITS.MAX_DEPTH) {
    return { files: [], tree: { name, children: [] } }
  }
  if (FOLDER_UPLOAD_LIMITS.SKIP_DIRS.includes(name)) {
    return { files: [], tree: { name, children: [] } }
  }

  if (entry.isDirectory) {
    const reader = entry.createReader!()
    const children = (await readAllEntries(reader)) as AnyEntry[]

    const files: File[] = []
    const childTrees: DirTree[] = []

    for (const child of children) {
      if (budget.filesEmitted >= budget.maxFiles) break

      if (child.isDirectory) {
        const sub = await walkDirectory(child, `${relRoot}/${child.name}`, depth + 1, budget)

        // Always include directories that exist in the file system
        // The backend will handle whether to create .qse.keep files or not
        childTrees.push(sub.tree)
        files.push(...sub.files)
      } else {
        const file: File = await new Promise((resolve, reject) =>
          (child as AnyEntry).file!(resolve, reject),
        )

        if (shouldFilterFile(file.name)) continue

        const relPath = `${relRoot}/${file.name}`

        setRelativePath(file, relPath)
        files.push(file)
        budget.filesEmitted += 1
      }
    }

    return { files, tree: { name, children: childTrees } }
  } else {
    // Rare: drop root is a file
    const file: File = await new Promise((resolve, reject) => entry.file!(resolve, reject))

    if (shouldFilterFile(file.name)) return { files: [], tree: { name, children: [] } }
    setRelativePath(file, `${relRoot}`)
    budget.filesEmitted += 1

    return { files: [file], tree: { name, children: [] } }
  }
}

interface FolderInfo {
  name: string
  files: File[]
  fileCount: number
  totalSize: number
  tree: { name: string; children?: any[] }
}

/**
 * Collect from DataTransfer:
 * - folders[] with their pure trees
 * - looseFiles[] (top-level files)
 * - files[] (combined for convenience)
 * - hasDirectory flag
 */
async function collectFromDataTransfer(
  dt: DataTransfer,
  onUploadBlocked?: () => void,
): Promise<{ files: File[]; hasDirectory: boolean; folders: FolderInfo[]; looseFiles: File[] }> {
  console.log('📂 [DragDrop] collectFromDataTransfer called:', {
    itemsCount: dt.items ? dt.items.length : 0,
    filesCount: dt.files ? dt.files.length : 0,
    types: Array.from(dt.types || []),
  })

  const items = dt.items ? Array.from(dt.items) : []
  const out: File[] = []
  const folders: FolderInfo[] = []
  const looseFiles: File[] = []
  let hasDirectory = false

  if (items.length) {
    const directoryEntries: AnyEntry[] = []
    const fileEntries: File[] = []

    for (const it of items.filter(item => item.kind === 'file')) {
      const entry = (it as any).webkitGetAsEntry?.() as AnyEntry | undefined

      if (entry) {
        if (entry.isDirectory) {
          hasDirectory = true
          directoryEntries.push(entry)
        } else {
          const f = it.getAsFile()

          if (f && !shouldFilterFile(f.name)) {
            fileEntries.push(f)
          }
        }
      } else {
        const f = it.getAsFile()

        if (f && !shouldFilterFile(f.name)) {
          fileEntries.push(f)
        }
      }
    }

    // Directories
    console.log('📂 [DragDrop] Processing directories:', directoryEntries.length)
    for (const entry of directoryEntries) {
      try {
        console.log('📂 [DragDrop] Processing directory:', entry.name)
        const fileCount = await countFilesInDirectory(entry as any)

        console.log('📂 [DragDrop] Directory file count:', { name: entry.name, count: fileCount })

        if (fileCount > FOLDER_UPLOAD_LIMITS.MAX_FILES) {
          toast.error(
            `Cannot upload folder "${entry.name}". Folder contains more than ${FOLDER_UPLOAD_LIMITS.MAX_FILES} files.`,
          )
          onUploadBlocked?.()
          continue
        }

        const { files: dirFiles, tree } = await walkDirectory(entry as any, entry.name)
        const totalSize = dirFiles.reduce((sum, file) => sum + file.size, 0)

        console.log('📂 [DragDrop] Directory processed:', {
          name: entry.name,
          filesCollected: dirFiles.length,
          totalSize,
          treeStructure: tree,
          filesDetails: dirFiles.map(f => ({
            name: f.name,
            path: (f as any).path,
            webkitRelativePath: (f as any).webkitRelativePath,
            size: f.size,
          })),
        })

        folders.push({
          name: entry.name,
          files: dirFiles,
          fileCount,
          totalSize,
          tree,
        })

        out.push(...dirFiles)
      } catch (error) {
        console.error(`Error processing folder "${entry.name}":`, error)
      }
    }

    // Loose files
    out.push(...fileEntries)
    looseFiles.push(...fileEntries)
  } else if (dt.files && dt.files.length) {
    const plain = Array.from(dt.files).filter(f => !shouldFilterFile(f.name))

    for (const f of plain) {
      if ((f as any).webkitRelativePath && (f as any).webkitRelativePath.includes('/')) {
        hasDirectory = true
      }
      out.push(f)
    }
    looseFiles.push(...plain)
  }

  if (
    !hasDirectory &&
    out.some(f => (f as any).webkitRelativePath && (f as any).webkitRelativePath.includes('/'))
  ) {
    hasDirectory = true
  }

  return { files: out, hasDirectory, folders, looseFiles }
}

// ───────────────────────────────────────────────────────────────────────────────
// Build/initiate folder tree (DnD variant) → get FolderInit + normalized files
// ───────────────────────────────────────────────────────────────────────────────

async function createFolderStructureFromDragDrop(
  files: (File & { path?: string })[],
  rootName: string,
  currentFolderId: string | undefined,
  parentPath: string,
  queryClient: any,
  treeOverride?: { name: string; children?: any[] },
): Promise<{ init: any; files: (File & { path?: string })[] } | null> {
  console.log('🌳 [DragDrop] createFolderStructureFromDragDrop called:', {
    rootName,
    filesCount: files.length,
    currentFolderId,
    parentPath,
    hasTreeOverride: !!treeOverride,
    timestamp: new Date().toISOString(),
    sampleFiles: files.slice(0, 3).map(f => ({
      name: f.name,
      path: (f as any).path || (f as any).webkitRelativePath,
      size: f.size,
    })),
    allFiles: files.map(f => ({
      name: f.name,
      path: (f as any).path || (f as any).webkitRelativePath,
      size: f.size,
    })),
  })

  // Build a tree if the browser didn't expose dirs for us
  const tree =
    treeOverride ??
    (() => {
      console.log('🌳 [DragDrop] Building tree from files...')
      const t: { name: string; children?: any[] } = { name: rootName, children: [] }
      const pathToNode = new Map<string, any>([[rootName, t]])

      for (const file of files) {
        const p = (file as any).path || (file as any).webkitRelativePath || file.name
        const parts = String(p).split('/').filter(Boolean)

        if (parts[0] === rootName) parts.shift()
        let cur = rootName
        let curNode = t

        for (const part of parts.slice(0, -1)) {
          cur = `${cur}/${part}`
          if (!pathToNode.has(cur)) {
            const newNode = { name: part, children: [] }

            curNode.children = curNode.children || []
            curNode.children.push(newNode)
            pathToNode.set(cur, newNode)
            console.log('🌳 [DragDrop] Created folder node:', { part, cur })
          }
          curNode = pathToNode.get(cur)
        }
      }

      console.log('🌳 [DragDrop] Final tree structure:', JSON.stringify(t, null, 2))

      return t
    })()

  // Create the picked result that mimics the FS Access API behavior
  const picked = { rootName, files, tree }

  console.log('🌳 [DragDrop] Calling initiateFolderTree...')

  const { result } = await initiateFolderTreeWithTracking({
    currentFolderId,
    parentPath,
    picked,
    queryClient,
  })

  console.log('🌳 [DragDrop] initiateFolderTree result:', {
    hasResult: !!result,
    hasInit: !!(result && 'init' in result),
    hasFiles: !!(result && 'files' in result),
    filesCount: result && 'files' in result ? result.files.length : 0,
    initDetails:
      result && 'init' in result
        ? {
            rootFolderId: result.init.rootFolderId,
            rootOriginalName: result.init.rootOriginalName,
            serverName: result.init.serverName,
            totalFiles: result.init.totalFiles,
            totalBytes: result.init.totalBytes,
          }
        : null,
  })

  // Check if quota was exceeded
  if (result && 'quotaExceeded' in result) {
    return null
  }

  return result
}

// ───────────────────────────────────────────────────────────────────────────────
// Hook
// ───────────────────────────────────────────────────────────────────────────────

interface UseFileExplorerDragDropProps {
  files: FileItem[]
  uploadEnabled?: boolean
  parentId?: string
  searchParams: any
  onFolderDrop: (fileIds: string[], targetFolderId: string | number) => Promise<void>
  setIsDragOver?: (isDragOver: boolean) => void
  pathname?: string
  parentType?: 'CHAT_ROOT' | 'CHAT_CONVERSATION'
}

export const useFileExplorerDragDrop = ({
  files,
  uploadEnabled = true,
  parentId,
  searchParams,
  onFolderDrop,
  setIsDragOver: externalSetIsDragOver,
  pathname = '/',
  parentType,
}: UseFileExplorerDragDropProps) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const { addFiles } = useProgressTrackerAdapter()
  const queryClient = useQueryClient()
  const isOverQuota = useIsOverQuota()

  // Calculate parent path based on current location
  const parentPath = pathname.startsWith('/folders/') ? pathname : '/'

  const handleDrop = useCallback(
    async (e: React.DragEvent<Element>, index: number) => {
      e.preventDefault()
      e.stopPropagation()

      // hide dropzone immediately on any drop
      setIsDragOver(false)
      externalSetIsDragOver?.(false)

      // Try to get the dragged file IDs (internal drag)
      let draggedIds: (string | number)[] = []

      try {
        const textData = e.dataTransfer.getData('text/plain')

        if (textData) {
          const parsed = JSON.parse(textData)

          if (Array.isArray(parsed)) draggedIds = parsed
        }
      } catch {}

      if (draggedIds.length === 0) {
        try {
          const customData = e.dataTransfer.getData('application/json')

          if (customData) {
            const parsed = JSON.parse(customData)

            if (parsed.fileIds && Array.isArray(parsed.fileIds)) {
              draggedIds = parsed.fileIds
            }
          }
        } catch {}
      }

      if (draggedIds.length > 0) {
        // Move existing files/folders within drive
        const targetFile = files[index]

        if (targetFile && targetFile.type === 'folder') {
          const draggedFiles = files.filter(f => draggedIds.includes(f.id))
          const isDroppingFolderIntoItself = draggedFiles.some(
            draggedFile =>
              draggedFile.id === targetFile.id ||
              (draggedFile.isDir &&
                draggedFile.path &&
                targetFile.path &&
                draggedFile.path === targetFile.path),
          )

          if (isDroppingFolderIntoItself) {
            return
          }

          const fileIds = draggedIds.map(id => id.toString())

          try {
            await onFolderDrop(fileIds, targetFile.id.toString())
          } catch {
            // handled upstream
          }
        }
      } else {
        // External drag (from desktop) - prevent if over quota
        if (isOverQuota) {
          toast.error('Storage quota exceeded. Delete files to free up space.')

          return
        }

        const {
          files: collected,
          hasDirectory,
          folders,
          looseFiles,
        } = await collectFromDataTransfer(e.dataTransfer, () => {
          setIsDragOver(false)
          externalSetIsDragOver?.(false)
        })

        // Where are we dropping?
        const target = e.target as HTMLElement
        const isDroppingOnFileCard =
          target.closest('.file-card') || target.closest('[data-file-id]')

        let targetParentId = parentId
        let targetParentPath = parentPath

        if (isDroppingOnFileCard) {
          const targetFile = files[index]

          if (targetFile && (targetFile.isDir || targetFile.type === 'folder')) {
            targetParentId = targetFile.id.toString()
            targetParentPath = targetFile.path || parentPath
          }
        }

        // Multi-folder
        if (folders.length > 1) {
          const shouldProceed = await showMultiFolderModal(folders)

          if (!shouldProceed) {
            return
          }

          for (const folder of folders) {
            try {
              const res = await createFolderStructureFromDragDrop(
                folder.files,
                folder.name,
                targetParentId,
                targetParentPath,
                queryClient,
                folder.tree,
              )

              if (res) {
                const { init, files: filesForFolder } = res

                addFiles(
                  filesForFolder,
                  targetParentId,
                  [queryKeys.files.list(searchParams)],
                  queryClient,
                  targetParentPath,
                  init,
                )
              }
            } catch (err) {
              console.error(`[MultiFolderUpload] Failed to queue "${folder.name}"`, err)
              toast.error(`Failed to queue "${folder.name}". Continuing with other folders.`)
            }
          }

          // Upload loose files (if any) as a regular upload
          if (looseFiles && looseFiles.length) {
            addFiles(
              looseFiles as any,
              targetParentId,
              [queryKeys.files.list(searchParams)],
              queryClient,
              targetParentPath,
            )
          }

          return
        }

        // Single folder (or mixed with loose files)
        if (hasDirectory && folders.length === 1) {
          console.log('📁 [DragDrop] Single folder upload detected:', {
            folderName: folders[0].name,
            filesCount: folders[0].files.length,
            fileCount: folders[0].fileCount,
            totalSize: folders[0].totalSize,
            targetParentId,
            targetParentPath,
            hasTree: !!folders[0].tree,
          })

          const folder = folders[0]
          const res = await createFolderStructureFromDragDrop(
            folder.files,
            folder.name,
            targetParentId,
            targetParentPath,
            queryClient,
            folder.tree,
          )

          console.log('📁 [DragDrop] createFolderStructureFromDragDrop result:', {
            hasResult: !!res,
            hasInit: !!res?.init,
            hasFiles: !!res?.files,
            filesCount: res?.files?.length || 0,
          })

          if (res) {
            const { init, files: filesForFolder } = res

            console.log('📁 [DragDrop] Calling addFiles with folder init:', {
              filesCount: filesForFolder.length,
              hasInit: !!init,
              initDetails: init
                ? {
                    rootFolderId: init.rootFolderId,
                    totalFiles: init.totalFiles,
                    totalBytes: init.totalBytes,
                  }
                : null,
            })

            addFiles(
              filesForFolder,
              targetParentId,
              [queryKeys.files.list(searchParams)],
              queryClient,
              targetParentPath,
              init,
            )
          } else {
            console.error('📁 [DragDrop] createFolderStructureFromDragDrop returned null!')
          }

          if (looseFiles && looseFiles.length) {
            console.log('📁 [DragDrop] Adding loose files:', looseFiles.length)
            addFiles(
              looseFiles as any,
              targetParentId,
              [queryKeys.files.list(searchParams)],
              queryClient,
              targetParentPath,
            )
          }

          return
        }

        // No folders → plain file upload
        if (collected.length > 0) {
          if (!uploadEnabled) return
          addFiles(
            collected as any,
            targetParentId,
            [queryKeys.files.list(searchParams)],
            queryClient,
            targetParentPath,
          )
        }
      }
    },
    [
      files,
      uploadEnabled,
      parentId,
      parentPath,
      searchParams,
      onFolderDrop,
      addFiles,
      queryClient,
      isOverQuota,
    ],
  )

  // Overlay drag area (outside file cards)
  const handleDragEvents = {
    onDragLeave: useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsDragOver(false)
          externalSetIsDragOver?.(false)
        }
      },
      [externalSetIsDragOver],
    ),

    onDragOver: useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        if (!uploadEnabled) return
        // Prevent drag and drop in system folders
        if (parentType) return
        // Prevent drag-over visual feedback when over quota
        if (isOverQuota) {
          e.dataTransfer.dropEffect = 'none'

          return
        }
        const isExternalDrag =
          e.dataTransfer.types.includes('Files') && !e.dataTransfer.types.includes('text/plain')

        if (isExternalDrag) setIsDragOver(true)
      },
      [uploadEnabled, parentType, isOverQuota],
    ),

    onDrop: useCallback(
      async (e: React.DragEvent) => {
        e.preventDefault()
        // hide dropzone immediately on overlay drop
        setIsDragOver(false)
        externalSetIsDragOver?.(false)
        if (!uploadEnabled) return
        // Prevent drag and drop in system folders
        if (parentType) {
          return
        }
        // Prevent external uploads when over quota
        if (isOverQuota) {
          toast.error('Storage quota exceeded. Delete files to free up space.')

          return
        }

        const {
          files: collected,
          hasDirectory,
          folders,
          looseFiles,
        } = await collectFromDataTransfer(e.dataTransfer, () => {
          setIsDragOver(false)
          externalSetIsDragOver?.(false)
        })

        // Multi-folder at overlay → upload each as its own job into current folder (or root)
        if (folders.length > 1) {
          const shouldProceed = await showMultiFolderModal(folders)

          if (!shouldProceed) {
            return
          }

          // Process folders sequentially to respect global concurrency limit
          for (const folder of folders) {
            try {
              const res = await createFolderStructureFromDragDrop(
                folder.files,
                folder.name,
                parentId,
                parentPath,
                queryClient,
                folder.tree,
              )

              if (res) {
                const { init, files: filesForFolder } = res

                console.log(
                  `🗂️ [MultiFolderUpload] Queuing folder "${folder.name}" with ${filesForFolder.length} files`,
                )
                addFiles(
                  filesForFolder,
                  parentId,
                  [queryKeys.files.list(searchParams)],
                  queryClient,
                  parentPath,
                  init,
                )
              }
            } catch (err) {
              console.error(`[MultiFolderUpload] Failed to queue "${folder.name}"`, err)
              toast.error(`Failed to queue "${folder.name}". Continuing with other folders.`)
            }
          }

          if (looseFiles && looseFiles.length) {
            addFiles(
              looseFiles as any,
              parentId,
              [queryKeys.files.list(searchParams)],
              queryClient,
              parentPath,
            )
          }

          return
        }

        // Single folder (or mixed) at overlay
        if (hasDirectory && folders.length === 1) {
          const folder = folders[0]
          const res = await createFolderStructureFromDragDrop(
            folder.files,
            folder.name,
            parentId,
            parentPath,
            queryClient,
            folder.tree,
          )

          if (res) {
            const { init, files: filesForFolder } = res

            addFiles(
              filesForFolder,
              parentId,
              [queryKeys.files.list(searchParams)],
              queryClient,
              parentPath,
              init,
            )
          }
          if (looseFiles && looseFiles.length) {
            addFiles(
              looseFiles as any,
              parentId,
              [queryKeys.files.list(searchParams)],
              queryClient,
              parentPath,
            )
          }

          return
        }

        // No folders → plain files into current folder/root
        if (collected.length > 0) {
          addFiles(
            collected as any,
            parentId,
            [queryKeys.files.list(searchParams)],
            queryClient,
            parentPath,
          )
        }
      },
      [
        addFiles,
        uploadEnabled,
        searchParams,
        queryClient,
        parentId,
        parentPath,
        externalSetIsDragOver,
        parentType,
        isOverQuota,
      ],
    ),
  }

  return {
    isDragOver,
    handleDrop,
    handleDragEvents,
  }
}
